import { useTranslation } from 'react-i18next'

import { relativeWindowInlineKey } from '@/features/play-intents/lib/playIntentWindows'
import { useProfileQuery } from '@/features/profile/hooks/useProfile'
import { cn } from '@/shared/lib/cn'
import { formatRelativeTime } from '@/shared/lib/formatRelativeTime'

import { notificationDecoration } from '../lib/notificationDecoration'
import {
  FAVORITE_GAME_SESSION_WITH_AREA_KEY,
  NOTIFICATION_FALLBACK_KEY,
  notificationTitleKey,
  usesServerText,
} from '../lib/notificationTitleKeys'
import type { NotificationResponse } from '../types/notification.types'

interface NotificationItemProps {
  notification: NotificationResponse
  onSelect: (notification: NotificationResponse) => void
}

/**
 * Item del dropdown. Si `readAt=null` lleva barra roja a la izquierda y
 * fondo destacado — patrón heredado de la lista de chats no leídos.
 * Click → marca como leída (vía el caller) + navega si la notif tiene
 * destino (p.ej. RATING_AVAILABLE → /sessions/:id/rate).
 */
export function NotificationItem({ notification, onSelect }: NotificationItemProps) {
  const { t, i18n } = useTranslation()
  const unread = notification.readAt === null
  const deco = notificationDecoration(notification.type)

  // Para NO_SHOW_COOLDOWN_ACTIVATED el copy necesita {{cooldownEnd}}, que
  // vive en el perfil del current user (no en la notif). Reusamos el cache
  // de useProfileQuery — si la notif llegó es porque el user está logueado
  // y su perfil ya está cargado (o cargándose) en otra parte de la app.
  const profileQuery = useProfileQuery({
    enabled: notification.type === 'NO_SHOW_COOLDOWN_ACTIVATED',
  })
  const cooldownEnd = formatCooldownEnd(profileQuery.data?.cooldownJoinUntil ?? null, i18n.language)
  // Tipo con plantilla propia. Si no (tipo nuevo para esta versión, o texto escrito por una
  // persona como Comunicaciones), se pinta el título y el texto que manda el servidor.
  const known =
    notificationTitleKey(notification.type) !== null && !usesServerText(notification.type)

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(notification)}
        className={cn(
          'relative flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors',
          unread ? 'bg-yellow/20 hover:bg-yellow/40' : 'opacity-80 hover:bg-yellow/20',
        )}
      >
        {unread && <span className="absolute inset-y-0 left-0 w-1 bg-red" aria-hidden="true" />}
        <span
          aria-hidden="true"
          className={cn(
            'chip flex size-9 shrink-0 items-center justify-center rounded-md',
            deco.bgClass,
            deco.textClass,
          )}
        >
          {deco.icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 break-words font-display text-sm font-bold leading-tight">
            {known
              ? formatTitle(t, notification, { cooldownEnd, language: i18n.language })
              : (notification.title ??
                t(notificationTitleKey(notification.type) ?? NOTIFICATION_FALLBACK_KEY))}
          </p>
          {subtitle(notification, known) && (
            <p className="mt-0.5 truncate font-brutal text-[10px] text-muted-foreground">
              {subtitle(notification, known)}
            </p>
          )}
          <p className="mt-1 font-brutal text-[9px] uppercase tracking-widest text-muted-foreground">
            {formatRelativeTime(notification.createdAt, i18n.language)}
          </p>
        </div>
      </button>
    </li>
  )
}

/**
 * Juego de la mesa para el copy: el juego BASE ("Catan"), no el título libre que
 * escribe el creador ("Catan el sábado"). Sin juego base (evento de tienda) → título.
 */
function gameName(notification: NotificationResponse): string {
  return notification.sessionBaseGameName ?? notification.sessionTitle ?? ''
}

/**
 * Segunda línea del item. Tipo conocido → el título de la mesa, pero solo si aporta
 * algo (no repite el juego que ya nombra el copy). Tipo desconocido → el cuerpo que
 * redactó el servidor.
 */
function subtitle(notification: NotificationResponse, known: boolean): string | null {
  if (!known) return notification.body ?? null
  // El nombre del evento ya va en el título del aviso: repetirlo debajo sobra.
  if (notification.type === 'STORE_EVENT_NEARBY' || notification.type === 'FOLLOWED_STORE_EVENT')
    return null
  const title = notification.sessionTitle?.trim()
  if (!title) return null
  const game = notification.sessionBaseGameName?.trim()
  return game && title.toLocaleLowerCase() === game.toLocaleLowerCase() ? null : title
}

function formatTitle(
  t: ReturnType<typeof useTranslation>['t'],
  notification: NotificationResponse,
  extras: { cooldownEnd: string; language: string },
): string {
  // Solo se llama con tipos conocidos (el desconocido pinta el texto del servidor).
  const titleKey = notificationTitleKey(notification.type) ?? NOTIFICATION_FALLBACK_KEY
  // FAVORITE_GAME_SESSION usa dos claves distintas según haya zona o no.
  // El resto comparte una sola clave con {{game}}.
  // Importante: {{game}} aquí es el NOMBRE DEL JUEGO base ("Catan"), no el
  // título libre que el creator pone a la sesión ("Catan Night"). Para los
  // demás tipos seguimos usando sessionTitle (ese era el contrato previo).
  // Si city y area coinciden (común en geo data ES — ej. "Abengibre")
  // mostramos ambos igualmente: "en Abengibre, Abengibre" es la copy
  // pedida por el usuario, deliberada.
  if (notification.type === 'FAVORITE_GAME_SESSION') {
    const city = notification.sessionCityName ?? ''
    const area = notification.sessionAreaName ?? ''
    const key = area ? FAVORITE_GAME_SESSION_WITH_AREA_KEY : titleKey
    return t(key, { game: gameName(notification), city, area })
  }
  // "Ganas de jugar": el grupo no tiene sesión — juego/ciudad/franja vienen en
  // los campos playIntent*. La franja se nombra relativa a hoy ("este finde" /
  // "el finde que viene").
  if (notification.type === 'PLAY_INTENT_GROUP') {
    const kind = notification.playIntentWindowKind
    const start = notification.playIntentWindowStart
    return t(titleKey, {
      size: notification.playIntentGroupSize ?? 2,
      game: notification.playIntentGameName ?? '',
      window:
        start && (kind === 'WEEKDAYS' || kind === 'WEEKEND')
          ? t(relativeWindowInlineKey(kind, start))
          : '',
      city: notification.playIntentCityName ?? '',
    })
  }
  if (notification.type === 'PLAY_INTENT_SESSION') {
    return t(titleKey, {
      game: gameName(notification),
      when: formatSessionWhen(notification.sessionScheduledAt, extras.language),
      city: notification.sessionCityName ?? '',
    })
  }
  return t(titleKey, {
    game: gameName(notification),
    when: formatSessionWhen(notification.sessionScheduledAt, extras.language),
    // Zona si la mesa tiene; si no, ciudad.
    place: notification.sessionAreaName ?? notification.sessionCityName ?? '',
    store: notification.storeName ?? '',
    // Local del evento (FOLLOWED_STORE_EVENT: lo que se sigue es el local); sin él, la tienda.
    venue: notification.sessionVenueName ?? notification.storeName ?? '',
    // Nombre que la tienda dio al evento (STORE_EVENT_NEARBY, FOLLOWED_STORE_EVENT).
    title: notification.sessionTitle ?? '',
    cooldownEnd: extras.cooldownEnd,
  })
}

/**
 * Formatea `cooldownJoinUntil` (ISO LocalDateTime serializado por el BE) a
 * un string legible. Vacío si no hay valor (el cooldown ya expiró, el user
 * no está bloqueado, o el perfil aún no se ha cargado).
 */
function formatCooldownEnd(iso: string | null, language: string): string {
  if (!iso) return ''
  const locale = language === 'es' ? 'es-ES' : 'en-GB'
  return new Date(iso).toLocaleString(locale, { dateStyle: 'long', timeStyle: 'short' })
}

/** "sábado, 4 oct, 18:00" — día y hora de la partida en la zona del usuario. */
function formatSessionWhen(iso: string | null, language: string): string {
  if (!iso) return ''
  const locale = language === 'es' ? 'es-ES' : 'en-GB'
  return new Date(iso).toLocaleString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
